/**
 * A tiny History API router: four kinds of page, no dependency.
 *
 *   /               Today (the default; anything unknown lands here too)
 *   /daily/:n       that daily's game (`?l=easy|medium|hard`)
 *   /play           Play: the stacks, the archive search (`?stack=&q=&sort=`, app/play-context.ts)
 *   /play/:id       a practice puzzle
 *   /you            your stats (and later your profile); the stats route
 *   /c/:code        a challenge ("You've been challenged"), then that daily
 *   /nest           the Nest tab (shown to everyone: nests/store.ts); /friends still opens it
 *   /n/:code        a nest invite (`?by=<memberId>`: who sent it), then today's Easy
 *   /privacy        what Sparrow keeps and sends (from About, in ≡)
 *
 * Anything else is Today, and the address is put right (`/`), so a reload or a copied link
 * doesn't carry the bad path (UX-022).
 *
 * Cloudflare serves the SPA for every path, so a deep link boots straight into its route.
 *
 * History is shaped so that back always means Today (docs/mockups/nav.html, "Back button"):
 * Today is the root; a cold game or challenge link gets a Today seeded underneath it; the
 * way home goes back instead of pushing; tabs don't pile up; and every sheet (the poster,
 * the menu, the guides) owns a state-only entry, so back closes it first. Each entry we
 * write carries the trail of paths below it (`history.state.stack`), which is how "is
 * Today below me, and how far?" is answered without guessing.
 */
import { isLevel, type Level } from '@arrow/puzzle-schema';
import { type AnchorHTMLAttributes, type MouseEvent, useEffect, useRef, useSyncExternalStore } from 'react';

import { contextEntries, type PlayContext, type PlayParams, playPath, readPlayContext, readPlayParams } from './play-context.ts';

export type Route =
	| { name: 'today' }
	| { name: 'daily'; number: number; level: Level | null; ctx?: PlayContext }
	| { name: 'play'; params?: PlayParams }
	| { name: 'practice'; id: string; level: Level | null; ctx?: PlayContext }
	| { name: 'stats' }
	| { name: 'challenge'; code: string }
	| { name: 'nest' }
	| { name: 'invite'; code: string; by: string | null }
	| { name: 'privacy' };

export type TabName = 'today' | 'play' | 'nest' | 'stats';

/** Reads a path (and its query) as a route. Never throws; unknown paths are Today. */
export function parseRoute(pathname: string, search = ''): Route {
	const parts = pathname.split('/').filter(Boolean);
	let level: Level | null = null;
	try {
		const l = new URLSearchParams(search).get('l');
		if (isLevel(l)) level = l;
	} catch {
		// No level.
	}
	const ctx = readPlayContext(search);
	const withCtx = ctx ? { ctx } : {};
	if (parts[0] === 'daily' && parts.length === 2) {
		const n = Number(parts[1]);
		if (Number.isInteger(n) && n > 0) return { name: 'daily', number: n, level, ...withCtx };
	}
	if (parts[0] === 'play') {
		if (parts.length === 1) {
			const params = readPlayParams(search);
			return params.stack || params.q || params.sort ? { name: 'play', params } : { name: 'play' };
		}
		if (parts.length === 2) {
			let id = parts[1]!;
			try {
				id = decodeURIComponent(id);
			} catch {
				// Keep it raw.
			}
			if (id && id.length <= 200) return { name: 'practice', id, level, ...withCtx };
		}
	}
	if ((parts[0] === 'you' || parts[0] === 'stats') && parts.length === 1) return { name: 'stats' };
	if ((parts[0] === 'friends' || parts[0] === 'nest') && parts.length === 1) return { name: 'nest' };
	if (parts[0] === 'n' && parts.length === 2 && /^[A-Za-z0-9]{6}$/.test(parts[1]!)) {
		let by: string | null = null;
		try {
			const b = new URLSearchParams(search).get('by');
			if (b && /^[0-9a-f-]{36}$/i.test(b)) by = b;
		} catch {
			// No tag.
		}
		return { name: 'invite', code: parts[1]!.toUpperCase(), by };
	}
	if (parts[0] === 'privacy' && parts.length === 1) return { name: 'privacy' };
	if (parts[0] === 'c' && parts.length === 2 && /^[A-Za-z0-9]{4,6}$/.test(parts[1]!)) return { name: 'challenge', code: parts[1]! };
	return { name: 'today' };
}

/** A path that isn't one of ours (`/daily/abc`, `/stats/extra`): it shows Today, and the address becomes `/`. */
export function isUnknownPath(pathname: string): boolean {
	if (pathname.split('/').filter(Boolean).length === 0) return false;
	return parseRoute(pathname).name === 'today';
}

/** A path with its query (`/daily/3?l=easy`) as a route. */
export function routeOf(path: string): Route {
	const q = path.indexOf('?');
	return parseRoute(q < 0 ? path : path.slice(0, q), q < 0 ? '' : path.slice(q));
}

/** The path for a route (what goes in the address bar). */
export function routePath(route: Route): string {
	switch (route.name) {
		case 'today':
			return '/';
		case 'daily':
			return `/daily/${route.number}${search([['l', route.level], ...contextEntries(route.ctx)])}`;
		case 'play':
			return playPath(route.params);
		case 'practice':
			return `/play/${encodeURIComponent(route.id)}${search([['l', route.level], ...contextEntries(route.ctx)])}`;
		case 'stats':
			return '/you';
		case 'challenge':
			return `/c/${route.code}`;
		case 'nest':
			return '/nest';
		case 'invite':
			return `/n/${route.code}${search([['by', route.by]])}`;
		case 'privacy':
			return '/privacy';
	}
}

function search(entries: Array<[string, string | null | undefined]>): string {
	const p = new URLSearchParams();
	for (const [k, v] of entries) if (v) p.set(k, v);
	const out = p.toString();
	return out ? `?${out}` : '';
}

/** Today, Play, Nest and Stats show the tab bar; a game hides it (the board is the hero). */
export function tabOf(route: Route): TabName | null {
	return route.name === 'today' || route.name === 'play' || route.name === 'nest' || route.name === 'stats' ? route.name : null;
}

/** A cold open of these gets a Today underneath it, so back shows Today before leaving. */
function wantsSeed(route: Route): boolean {
	return route.name === 'daily' || route.name === 'practice' || route.name === 'challenge' || route.name === 'invite' || route.name === 'privacy';
}

// --- History: Today is the root ------------------------------------------------------

/** What the router needs from `window.history` (a fake one in tests). */
export interface HistoryEnv {
	state(): unknown;
	/** pathname + search. */
	path(): string;
	push(state: unknown, url: string): void;
	replace(state: unknown, url: string): void;
	/** Asynchronous in a browser: the move lands with a `popstate`. */
	go(delta: number): void;
	/** Listens for `popstate`; returns the unsubscribe. */
	listen(fn: () => void): () => void;
	/** Runs `fn` after the current task (a sheet closing and another opening in one tap). */
	defer(fn: () => void): void;
	/** Full navigation, when the History API refuses. */
	assign?(url: string): void;
}

/** The trail entry of a sheet: same URL as the screen under it, state only. */
export const OVERLAY = '#sheet';
/** Trails are capped; anything older than this many entries is simply forgotten. */
const MAX_TRAIL = 40;

interface NavState {
	sparrow: 1;
	/** Every path from the first entry we wrote up to this one (this one last). */
	stack: string[];
}

function isNavState(s: unknown): s is NavState {
	if (!s || typeof s !== 'object') return false;
	const o = s as Partial<NavState>;
	return o.sparrow === 1 && Array.isArray(o.stack) && o.stack.length > 0 && o.stack.every((p) => typeof p === 'string');
}

/** One overlay that's open: closed when back leaves its entry. */
export interface OverlayHandle {
	close(): void;
}

export interface Nav {
	/** At boot: tag the entry we opened on, seeding Today under a cold game or challenge. */
	seed(): void;
	/** The trail as the current entry records it (the screen's own path last, any sheet after). */
	stack(): string[];
	navigate(path: string, opts?: { replace?: boolean }): void;
	/** Back to `target` (`/` or `/play`): back through history when it's below, else replace. */
	goUp(target: string): void;
	/** Back to the screen below this one (a leaf page's "‹ Back"), or Today if there's none of ours. */
	back(): void;
	/** The tab bar: Today goes back to Today; Play, Nest and Stats replace each other; from Today they push. */
	goTab(path: string): void;
	/**
	 * A sheet opens. Sheets share one state-only entry; a `nested` one (a view inside an
	 * open sheet, as Settings inside ≡) pushes one more, so back steps out of it first.
	 */
	openOverlay(handle: OverlayHandle, nested?: boolean): void;
	releaseOverlay(handle: OverlayHandle): void;
	/** Path changes since boot (pushes and back/forward), for "is this still the share open?". */
	moves(): number;
	subscribe(fn: () => void): () => void;
	notify(): void;
	dispose(): void;
}

export function createNav(env: HistoryEnv): Nav {
	const listeners = new Set<() => void>();
	const overlays = new Set<OverlayHandle>();
	/** The nested ones, oldest first: each owns one more sheet entry on top of the shared one. */
	const nested: OverlayHandle[] = [];
	/** How many sheet entries the open sheets account for. */
	const levels = () => (overlays.size === 0 ? 0 : 1 + nested.length);
	let pending: (() => void) | null = null;
	let traversing = false;
	let moves = 0;
	let lastPath = env.path();

	const stack = (): string[] => {
		const raw = env.state();
		const path = env.path();
		if (!isNavState(raw)) return [path];
		const s = [...raw.stack];
		// Something else rewrote the address (share params, the level): the top is here.
		if (s[s.length - 1] !== OVERLAY) s[s.length - 1] = path;
		return s;
	};
	const write = (how: 'push' | 'replace', next: string[], url: string): boolean => {
		const state: NavState = { sparrow: 1, stack: next.slice(-MAX_TRAIL) };
		try {
			if (how === 'push') env.push(state, url);
			else env.replace(state, url);
			return true;
		} catch {
			env.assign?.(url);
			return false;
		}
	};
	const notify = () => {
		for (const fn of [...listeners]) fn();
	};
	const traverse = (delta: number, then: (() => void) | null = null) => {
		pending = then;
		traversing = true;
		env.go(delta);
	};
	/** Index of the screen's own entry in `s` (below any sheet entries). */
	const screenAt = (s: string[]) => {
		let i = s.length - 1;
		while (i > 0 && s[i] === OVERLAY) i--;
		return i;
	};
	/** Leave any sheet entries first, then `then` (no stale sheet entry is ever left behind). */
	const settle = (then: () => void) => {
		const s = stack();
		const n = s.length - 1 - screenAt(s);
		if (n === 0) then();
		else traverse(-n, then);
	};

	const onPop = () => {
		traversing = false;
		const run = pending;
		pending = null;
		const s = stack();
		const depth = s.length - 1 - screenAt(s);
		// Back left the sheet's entry: every open sheet closes.
		if (depth === 0) {
			const open = [...overlays];
			overlays.clear();
			nested.length = 0;
			for (const o of open) o.close();
		} else if (depth < levels()) {
			// Back left a nested view's entry (Settings → the menu): that view closes, newest first.
			while (nested.length > 0 && depth < levels()) {
				const o = nested.pop()!;
				overlays.delete(o);
				o.close();
			}
		} else if (depth > levels() && !run) {
			// Landed on a sheet entry with no sheet (forward, or a reload): step off it.
			traverse(levels() - depth);
			return;
		}
		run?.();
		const now = env.path();
		if (now !== lastPath) {
			moves++;
			lastPath = now;
		}
		notify();
	};
	const unlisten = env.listen(onPop);

	const navigate = (path: string, opts: { replace?: boolean } = {}) =>
		settle(() => {
			const s = stack();
			if (path === env.path() && !opts.replace) return;
			const next = opts.replace ? [...s.slice(0, -1), path] : [...s, path];
			if (!write(opts.replace ? 'replace' : 'push', next, path)) return;
			if (!opts.replace) moves++;
			lastPath = path;
			notify();
		});

	const goUp = (target: string) => {
		const s = stack();
		const at = screenAt(s);
		if (s[at] === target) return settle(() => {});
		const j = at > 0 ? s.lastIndexOf(target, at - 1) : -1;
		if (j >= 0) traverse(j - (s.length - 1));
		else navigate(target, { replace: true });
	};

	return {
		seed() {
			const raw = env.state();
			const path = env.path();
			const q = path.indexOf('?');
			// Not one of our paths: Today, and the address says so.
			if (isUnknownPath(q < 0 ? path : path.slice(0, q))) {
				write('replace', ['/'], '/');
				lastPath = env.path();
				return;
			}
			if (isNavState(raw)) {
				// A reload. A sheet's entry has no sheet any more: step back off it.
				if (raw.stack[raw.stack.length - 1] === OVERLAY) traverse(-1);
				else if (raw.stack[raw.stack.length - 1] !== path) write('replace', [...raw.stack.slice(0, -1), path], path);
				return;
			}
			if (wantsSeed(routeOf(path))) {
				if (write('replace', ['/'], '/')) write('push', ['/', path], path);
			} else write('replace', [path], path);
			lastPath = env.path();
		},
		stack,
		navigate,
		goUp,
		back() {
			const s = stack();
			const at = screenAt(s);
			if (at > 0) traverse(at - 1 - (s.length - 1));
			else navigate('/', { replace: true });
		},
		goTab(path) {
			if (path === '/') return goUp('/');
			const s = stack();
			const here = routeOf(s[screenAt(s)]!);
			// Play's tab inside Play (an open stack, a search): back to the stacks.
			if (path === '/play' && here.name === 'play') return goUp('/play');
			const fromTab = here.name === 'play' || here.name === 'nest' || here.name === 'stats';
			navigate(path, { replace: fromTab });
		},
		openOverlay(handle, isNested = false) {
			if (overlays.has(handle)) return;
			overlays.add(handle);
			if (isNested) nested.push(handle);
			if (traversing) return;
			const s = stack();
			const depth = s.length - 1 - screenAt(s);
			if (depth < levels()) write('push', [...s, OVERLAY], env.path());
		},
		releaseOverlay(handle) {
			if (!overlays.delete(handle)) return;
			const i = nested.indexOf(handle);
			if (i >= 0) nested.splice(i, 1);
			// A sheet closed itself (✕, a tap outside, an item, ‹ back to the menu): take its
			// entries off, unless another sheet took over in the same tap or back already did it.
			env.defer(() => {
				if (traversing) return;
				const s = stack();
				const extra = s.length - 1 - screenAt(s) - levels();
				if (extra > 0) traverse(-extra);
			});
		},
		moves: () => moves,
		subscribe(fn) {
			listeners.add(fn);
			return () => listeners.delete(fn);
		},
		notify,
		dispose: unlisten,
	};
}

const windowEnv: HistoryEnv = {
	state: () => window.history.state as unknown,
	path: () => `${window.location.pathname}${window.location.search}`,
	push: (state, url) => window.history.pushState(state, '', url),
	replace: (state, url) => window.history.replaceState(state, '', url),
	go: (delta) => window.history.go(delta),
	listen: (fn) => {
		window.addEventListener('popstate', fn);
		return () => window.removeEventListener('popstate', fn);
	},
	defer: (fn) => void window.setTimeout(fn, 0),
	assign: (url) => window.location.assign(url),
};

const nav: Nav | null = typeof window === 'undefined' ? null : createNav(windowEnv);

/** How many times the app has changed page (or the browser went back/forward) since it opened. */
export function navigations(): number {
	return nav?.moves() ?? 0;
}

/** Once, at boot (after the share params are read): seed Today under a cold deep link. */
export function seedHistory(): void {
	nav?.seed();
}

/** Goes to `path` (a new history entry, or `replace` the current one). Closes any sheet first. */
export function navigate(path: string, opts: { replace?: boolean } = {}): void {
	nav?.navigate(path, opts);
}

/**
 * "‹ Today" / "‹ Play": back to that screen if it's below this one, else replace. Never a
 * push. A Play target can carry its stack or search (`/play?stack=missed`).
 */
export function goUp(target: string): void {
	nav?.goUp(target);
}

/** "‹ Back" on a leaf page: the screen you came from, else Today. */
export function goBack(): void {
	nav?.back();
}

/** A tab bar tap. */
export function goTab(path: string): void {
	nav?.goTab(path);
}

const getSnapshot = () => (typeof window === 'undefined' ? '/' : `${window.location.pathname}${window.location.search}`);
const subscribe = (fn: () => void) => nav?.subscribe(fn) ?? (() => {});

/** The current route; re-renders on navigation and the browser's back/forward. */
export function useRoute(): Route {
	return routeOf(useSyncExternalStore(subscribe, getSnapshot, getSnapshot));
}

/** Re-reads the address bar (after something else rewrote it, e.g. `readShareParams`). */
export function syncLocation(): void {
	nav?.notify();
}

/** Which page a route shows, for "did the page change?" (a stack or search inside Play doesn't). */
function pageKey(route: Route): string {
	switch (route.name) {
		case 'daily':
			return `daily:${route.number}:${route.level ?? ''}`;
		case 'practice':
			return `practice:${route.id}`;
		case 'challenge':
			return `challenge:${route.code}`;
		case 'invite':
			return `invite:${route.code}`;
		default:
			return route.name;
	}
}

/** How long a new page may take to put its heading up (it may be loading) before we stop looking. */
const ROUTE_FOCUS_WAIT_MS = 4000;

/**
 * After the page changes (not the first page, and not into a game, which focuses its own
 * grid), focus moves to the new page's `h1`, so a keyboard user starts from the top and a
 * screen reader says where they are (A11Y-7, UX-017). It waits for the heading if the page
 * is still loading, and leaves focus alone if the page (or the player) has already put it
 * somewhere. The heading takes `tabindex="-1"` and draws no ring (base.css).
 */
export function useRouteFocus(route: Route): void {
	const key = pageKey(route);
	const game = route.name === 'daily' || route.name === 'practice';
	const shown = useRef(key);
	useEffect(() => {
		if (shown.current === key) return;
		shown.current = key;
		if (game || typeof document === 'undefined') return;
		const root = document.getElementById('root') ?? document.body;
		const started = Date.now();
		let timer = 0;
		const settle = () => {
			const here = document.activeElement;
			// Something in the new page already has focus (its own focus, or the player's): leave it.
			if (here && here !== document.body && root.contains(here)) return;
			const h1 = [...root.querySelectorAll<HTMLElement>('h1')].find((h) => !h.closest('[aria-busy="true"], [inert]'));
			if (h1) {
				if (!h1.hasAttribute('tabindex')) h1.setAttribute('tabindex', '-1');
				h1.focus({ preventScroll: true });
				return;
			}
			if (Date.now() - started < ROUTE_FOCUS_WAIT_MS) timer = window.setTimeout(settle, 50);
		};
		timer = window.setTimeout(settle, 0);
		return () => window.clearTimeout(timer);
	}, [key, game]);
}

/**
 * A sheet's history entry: while `open`, the sheet owns a state-only entry, so back (Android,
 * the Safari swipe) closes it and calls `close`. Closing it any other way (✕, the dim, an
 * item) takes the entry off again with `history.back()`. Same URL throughout. `nested`: a
 * view inside a sheet that's already open (≡ → Settings) gets an entry of its own, so back
 * steps Settings → the menu → the page (QA2-11).
 */
export function useOverlay(open: boolean, close: () => void, { nested = false }: { nested?: boolean } = {}): void {
	const closeRef = useRef(close);
	closeRef.current = close;
	useEffect(() => {
		if (!open || !nav) return;
		const handle: OverlayHandle = { close: () => closeRef.current() };
		nav.openOverlay(handle, nested);
		return () => nav.releaseOverlay(handle);
	}, [open, nested]);
}

/**
 * A same-app link: a real `<a href>` (middle-click, copy link) that navigates in place.
 * `how`: `push` (a new page), `tab` (the tab bar's rules) or `up` (back to it if it's below).
 */
export function Link({
	to,
	how = 'push',
	onNavigate,
	...rest
}: AnchorHTMLAttributes<HTMLAnchorElement> & { to: string; how?: 'push' | 'tab' | 'up'; onNavigate?: () => void }) {
	const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
		rest.onClick?.(e);
		if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
		e.preventDefault();
		onNavigate?.();
		if (how === 'tab') goTab(to);
		else if (how === 'up' && (to === '/' || to.startsWith('/play'))) goUp(to);
		else navigate(to);
	};
	return <a {...rest} href={to} onClick={onClick} />;
}
